import React, { useEffect, useState } from "react";
import { useI18n } from "../i18n";
import { api } from "../lib/api";
import { getStoredUser } from "../components/RegisterModal";
import { Plus, Trash2, Bell, BellRing, Crown, Star } from "lucide-react";
import { Link } from "react-router-dom";

const ASSET_TYPES = [
  { id: "currency", pt: "Moeda", en: "Currency" },
  { id: "crypto", pt: "Cripto", en: "Crypto" },
  { id: "index", pt: "Índice", en: "Index" },
  { id: "real_estate", pt: "Imóvel (cidade)", en: "Real estate (city)" },
  { id: "country", pt: "País (macro)", en: "Country (macro)" },
];

export default function WatchlistAlerts() {
  const { lang } = useI18n();
  const pt = lang === "pt";
  const user = getStoredUser();
  const email = user?.email || null;

  const [tab, setTab] = useState("watchlist");
  const [watch, setWatch] = useState([]);
  const [alerts, setAlerts] = useState({ rules: [], notifications: [], unread: 0 });
  const [needsPro, setNeedsPro] = useState(false);

  const [wForm, setWForm] = useState({ asset_type: "crypto", asset_id: "", label: "", note: "" });
  const [aForm, setAForm] = useState({ kind: "crypto", asset_id: "", direction: "above", threshold: "", note: "" });
  const [busy, setBusy] = useState(false);

  const loadAll = async () => {
    if (!email) return;
    // First check the plan via /usage/me; if not Pro/Elite, show upgrade panel up-front.
    try {
      const u = await api.get(`/usage/me?email=${encodeURIComponent(email)}`);
      const plan = u.data?.plan;
      const active = u.data?.active;
      if (!active || !["pro", "elite"].includes(plan)) {
        setNeedsPro(true);
        return;
      }
    } catch {
      // fall-through to load attempt; 402 will flip needsPro
    }
    try {
      const w = await api.get(`/watchlist?email=${encodeURIComponent(email)}`);
      setWatch(w.data.items || []);
      const a = await api.get(`/alerts?email=${encodeURIComponent(email)}`);
      setAlerts(a.data);
      // also check alerts on each load (Pro+ users only)
      api.post("/alerts/check").catch(() => {});
    } catch (e) {
      if (e?.response?.status === 402) setNeedsPro(true);
    }
  };

  useEffect(() => { loadAll(); }, [email]);

  const addWatch = async (e) => {
    e.preventDefault();
    if (!wForm.asset_id || !email) return;
    setBusy(true);
    try {
      await api.post("/watchlist", { email, ...wForm });
      setWForm({ asset_type: "crypto", asset_id: "", label: "", note: "" });
      loadAll();
    } catch (err) {
      if (err?.response?.status === 402) setNeedsPro(true);
    } finally { setBusy(false); }
  };

  const removeWatch = async (id) => {
    await api.delete(`/watchlist/${id}?email=${encodeURIComponent(email)}`);
    loadAll();
  };

  const addAlert = async (e) => {
    e.preventDefault();
    if (!aForm.asset_id || !aForm.threshold || !email) return;
    setBusy(true);
    try {
      await api.post("/alerts", { email, ...aForm, threshold: Number(aForm.threshold) });
      setAForm({ kind: "crypto", asset_id: "", direction: "above", threshold: "", note: "" });
      loadAll();
    } catch (err) {
      if (err?.response?.status === 402) setNeedsPro(true);
    } finally { setBusy(false); }
  };

  const removeAlert = async (id) => {
    await api.delete(`/alerts/${id}?email=${encodeURIComponent(email)}`);
    loadAll();
  };

  if (!email) {
    return (
      <div className="max-w-[900px] mx-auto px-6 lg:px-10 pt-28 pb-16" data-testid="page-watchlist">
        <h1 className="font-display text-4xl tracking-tight">{pt ? "Watchlist e Alertas" : "Watchlist & Alerts"}</h1>
        <p className="mt-3 text-neutral-400">{pt ? "Faça login para acessar." : "Sign in to access."}</p>
      </div>
    );
  }

  if (needsPro) {
    return (
      <div className="max-w-[900px] mx-auto px-6 lg:px-10 pt-28 pb-16" data-testid="page-watchlist">
        <div className="surface p-10 text-center border border-gold-500/30">
          <Crown size={32} className="text-gold-400 mx-auto" />
          <h1 className="mt-4 font-display text-3xl tracking-tight">{pt ? "Watchlist e Alertas — Pro" : "Watchlist & Alerts — Pro"}</h1>
          <p className="mt-3 text-neutral-300 max-w-xl mx-auto">
            {pt
              ? "Salve até 20 ativos no Pro (ilimitado no Elite) e configure alertas in-app: 'BTC acima de $200k', 'Lisboa imóvel cair 5%', etc."
              : "Save up to 20 assets on Pro (unlimited on Elite) and configure in-app alerts: 'BTC above $200k', 'Lisbon real-estate drops 5%', etc."}
          </p>
          <Link to="/planos" data-testid="watchlist-upgrade" className="mt-6 inline-flex items-center gap-2 bg-gold-500 hover:bg-gold-400 text-black font-medium px-6 py-3 rounded-md transition-colors">
            <Crown size={14} /> {pt ? "Ver planos" : "View plans"}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-10 pt-28 pb-16" data-testid="page-watchlist">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <span className="text-xs uppercase tracking-[0.2em] text-gold-400 font-mono">PRO</span>
          <h1 className="mt-2 font-display text-4xl tracking-tight">{pt ? "Watchlist e Alertas" : "Watchlist & Alerts"}</h1>
          <p className="mt-2 text-neutral-400">
            {pt ? "Acompanhe seus ativos favoritos e seja notificado quando o mercado mexer." : "Track your favorite assets and get notified when the market moves."}
          </p>
        </div>
        {alerts.unread > 0 && (
          <div className="flex items-center gap-2 bg-gold-500/10 border border-gold-500/40 px-3 py-2 rounded-md text-sm">
            <BellRing size={14} className="text-gold-400" />
            <span>{alerts.unread} {pt ? "novos alertas" : "new alerts"}</span>
          </div>
        )}
      </div>

      <div className="mt-6 flex gap-2 border-b border-white/10">
        <TabBtn active={tab === "watchlist"} onClick={() => setTab("watchlist")} testid="tab-watchlist" label={pt ? "Watchlist" : "Watchlist"} count={watch.length} />
        <TabBtn active={tab === "alerts"} onClick={() => setTab("alerts")} testid="tab-alerts" label={pt ? "Alertas" : "Alerts"} count={alerts.rules.length} />
        <TabBtn active={tab === "notifications"} onClick={() => setTab("notifications")} testid="tab-notifications" label={pt ? "Notificações" : "Notifications"} count={alerts.notifications.length} />
      </div>

      {tab === "watchlist" && (
        <div className="mt-6 space-y-6">
          <form onSubmit={addWatch} className="surface p-5 grid sm:grid-cols-5 gap-3" data-testid="watch-form">
            <select value={wForm.asset_type} onChange={(e) => setWForm({ ...wForm, asset_type: e.target.value })} className="bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="watch-type">
              {ASSET_TYPES.map((t) => <option key={t.id} value={t.id}>{pt ? t.pt : t.en}</option>)}
            </select>
            <input value={wForm.asset_id} onChange={(e) => setWForm({ ...wForm, asset_id: e.target.value })} placeholder={pt ? "ID (BTC, SPX, Miami...)" : "ID (BTC, SPX, Miami...)"} className="bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="watch-id" />
            <input value={wForm.label} onChange={(e) => setWForm({ ...wForm, label: e.target.value })} placeholder={pt ? "Apelido (opcional)" : "Label (optional)"} className="bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="watch-label" />
            <input value={wForm.note} onChange={(e) => setWForm({ ...wForm, note: e.target.value })} placeholder={pt ? "Nota (opcional)" : "Note (optional)"} className="bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm sm:col-span-1" data-testid="watch-note" />
            <button type="submit" disabled={busy} className="bg-gold-500 hover:bg-gold-400 text-black text-sm font-medium px-4 py-2 rounded-md flex items-center justify-center gap-2 transition-colors disabled:opacity-50" data-testid="watch-add">
              <Plus size={14} /> {pt ? "Adicionar" : "Add"}
            </button>
          </form>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="watch-list">
            {watch.length === 0 && <p className="text-neutral-500 text-sm">{pt ? "Sua watchlist está vazia." : "Your watchlist is empty."}</p>}
            {watch.map((w) => (
              <div key={w.id} className="surface p-4 flex items-start justify-between" data-testid={`watch-item-${w.id}`}>
                <div>
                  <div className="flex items-center gap-2">
                    <Star size={12} className="text-gold-400" />
                    <span className="font-display text-lg">{w.label || w.asset_id}</span>
                  </div>
                  <div className="text-[11px] font-mono uppercase tracking-widest text-neutral-500 mt-1">{w.asset_type}</div>
                  {w.note && <div className="text-xs text-neutral-400 mt-2">{w.note}</div>}
                </div>
                <button onClick={() => removeWatch(w.id)} className="text-neutral-500 hover:text-red-400 p-1" aria-label="remove" data-testid={`watch-remove-${w.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "alerts" && (
        <div className="mt-6 space-y-6">
          <form onSubmit={addAlert} className="surface p-5 grid sm:grid-cols-6 gap-3" data-testid="alert-form">
            <select value={aForm.kind} onChange={(e) => setAForm({ ...aForm, kind: e.target.value })} className="bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="alert-kind">
              <option value="crypto">{pt ? "Cripto" : "Crypto"}</option>
              <option value="fx">{pt ? "Câmbio" : "FX"}</option>
              <option value="index">{pt ? "Índice" : "Index"}</option>
            </select>
            <input value={aForm.asset_id} onChange={(e) => setAForm({ ...aForm, asset_id: e.target.value })} placeholder="BTC, BRL, SPX" className="bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="alert-asset" />
            <select value={aForm.direction} onChange={(e) => setAForm({ ...aForm, direction: e.target.value })} className="bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="alert-direction">
              <option value="above">{pt ? "Acima de" : "Above"}</option>
              <option value="below">{pt ? "Abaixo de" : "Below"}</option>
            </select>
            <input type="number" step="0.0001" value={aForm.threshold} onChange={(e) => setAForm({ ...aForm, threshold: e.target.value })} placeholder={pt ? "Valor" : "Value"} className="bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="alert-threshold" />
            <input value={aForm.note} onChange={(e) => setAForm({ ...aForm, note: e.target.value })} placeholder={pt ? "Nota" : "Note"} className="bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="alert-note" />
            <button type="submit" disabled={busy} className="bg-gold-500 hover:bg-gold-400 text-black text-sm font-medium px-4 py-2 rounded-md flex items-center justify-center gap-2 transition-colors disabled:opacity-50" data-testid="alert-add">
              <Plus size={14} /> {pt ? "Criar" : "Create"}
            </button>
          </form>
          <div className="grid sm:grid-cols-2 gap-3" data-testid="alert-list">
            {alerts.rules.length === 0 && <p className="text-neutral-500 text-sm">{pt ? "Nenhum alerta configurado." : "No alerts set yet."}</p>}
            {alerts.rules.map((r) => (
              <div key={r.id} className="surface p-4 flex items-start justify-between" data-testid={`alert-item-${r.id}`}>
                <div>
                  <div className="flex items-center gap-2">
                    <Bell size={12} className="text-gold-400" />
                    <span className="font-display text-lg">{r.asset_id}</span>
                    <span className="text-xs font-mono uppercase tracking-widest text-neutral-500">{r.kind}</span>
                  </div>
                  <div className="text-sm mt-1.5">
                    {pt ? "Avise quando" : "Notify when"} <span className="text-gold-300">{r.direction === "above" ? (pt ? "acima de" : "above") : (pt ? "abaixo de" : "below")} {r.threshold}</span>
                  </div>
                  {r.note && <div className="text-xs text-neutral-400 mt-2">{r.note}</div>}
                </div>
                <button onClick={() => removeAlert(r.id)} className="text-neutral-500 hover:text-red-400 p-1" aria-label="remove" data-testid={`alert-remove-${r.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "notifications" && (
        <div className="mt-6 space-y-2" data-testid="notif-list">
          {alerts.notifications.length === 0 && <p className="text-neutral-500 text-sm">{pt ? "Nenhuma notificação." : "No notifications."}</p>}
          {alerts.notifications.map((n) => (
            <div key={n.id} className={`surface p-4 flex items-center justify-between ${n.read ? "opacity-60" : ""}`} data-testid={`notif-${n.id}`}>
              <div>
                <div className="font-display text-base">{n.title}</div>
                <div className="text-xs text-neutral-500 mt-1 font-mono">
                  {n.kind} · {pt ? "valor atual" : "current"}: {n.current_value}
                </div>
              </div>
              <div className="text-[11px] text-neutral-600 font-mono">{new Date(n.created_at).toLocaleString()}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const TabBtn = ({ active, onClick, label, count, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`px-4 py-2 -mb-px border-b-2 text-sm font-mono uppercase tracking-widest transition-colors ${
      active ? "border-gold-500 text-gold-300" : "border-transparent text-neutral-500 hover:text-white"
    }`}
  >
    {label} <span className="text-xs">({count})</span>
  </button>
);
